import { useState, useMemo } from 'react';
import { AppCard } from '@/components/ui/AppCard';
import AppButton from '@/components/ui/AppButton';
import AppInput from '@/components/ui/AppInput';
import { endpoints } from '@/services/endpoints';
import {
  axiosGetRequest,
  axiosPostRequest,
  axiosPutRequest,
  axiosDeleteRequest,
} from '@/services/Request';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Pencil, Trash2, Plus, X, Tag, Save, Search } from 'lucide-react';
import { toast } from 'react-toastify';
import AppPagination from '@/components/ui/AppPagination';
import { Skeleton } from '@/components/ui/Skeleton';
import { TableFilterBar, type FilterConfig } from '@/components/ui/TableFilterBar';


const SORT_CONFIGS: FilterConfig[] = [
  {
    key: 'sort',
    label: 'Sort By',
    options: [
      { value: '', label: 'Default order' },
      { value: 'name_asc', label: 'Name A–Z' },
      { value: 'name_desc', label: 'Name Z–A' },
      { value: 'books_desc', label: 'Most books' },
      { value: 'books_asc', label: 'Fewest books' },
    ],
  },
];

interface ICategory {
  _id: string;
  name_en: string;
  name_ar: string;
  index?: number;
  booksCount?: number;
}

export default function Categories() {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [showModal, setShowModal] = useState(false);
  const [editTarget, setEditTarget] = useState<ICategory | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<ICategory | null>(null);
  const [form, setForm] = useState({ name_en: '', name_ar: '' });
  const [localSearch, setLocalSearch] = useState('');
  const [sortValues, setSortValues] = useState<Record<string, string>>({ sort: '' });

  const { data, isLoading } = useQuery({
    queryKey: [endpoints.getAllCategories, page],
    queryFn: () => axiosGetRequest(endpoints.getAllCategories, { page, limit: 15, booksLimit: 0 }),
  });

  const addCategory = useMutation({
    mutationFn: (body: any) => axiosPostRequest(endpoints.addCategory, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [endpoints.getAllCategories] });
      toast.success('Category added');
      closeModal();
    },
    onError: (e: any) => toast.error(e?.message || 'Failed to add category'),
  });

  const updateCategory = useMutation({
    mutationFn: ({ id, body }: { id: string; body: any }) =>
      axiosPutRequest(`${endpoints.updateCategory}?id=${id}`, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [endpoints.getAllCategories] });
      toast.success('Category updated');
      closeModal();
    },
    onError: (e: any) => toast.error(e?.message || 'Failed to update category'),
  });

  const deleteCategory = useMutation({
    mutationFn: (id: string) => axiosDeleteRequest(endpoints.deleteCategory, { id }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [endpoints.getAllCategories] });
      toast.success('Category deleted');
      setDeleteTarget(null);
    },
    onError: (e: any) => toast.error(e?.message || 'Failed to delete category'),
  });

  function openAdd() {
    setForm({ name_en: '', name_ar: '' });
    setEditTarget(null);
    setShowModal(true);
  }

  function openEdit(cat: ICategory) {
    setForm({ name_en: cat.name_en, name_ar: cat.name_ar });
    setEditTarget(cat);
    setShowModal(true);
  }

  function closeModal() {
    setShowModal(false);
    setEditTarget(null);
    setForm({ name_en: '', name_ar: '' });
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!form.name_en.trim() || !form.name_ar.trim()) {
      toast.error('Both English and Arabic names are required');
      return;
    }
    if (editTarget) {
      updateCategory.mutate({ id: editTarget._id, body: form });
    } else {
      addCategory.mutate(form);
    }
  }

  const rawCategories: ICategory[] = data?.result || [];

  // Client-side search + sort on the current page
  const categories = useMemo(() => {
    let items = rawCategories;
    if (localSearch.trim()) {
      const q = localSearch.toLowerCase();
      items = items.filter(
        c => c.name_en.toLowerCase().includes(q) || c.name_ar.includes(q)
      );
    }
    const sort = sortValues.sort;
    if (sort === 'name_asc') items = [...items].sort((a, b) => a.name_en.localeCompare(b.name_en));
    else if (sort === 'name_desc') items = [...items].sort((a, b) => b.name_en.localeCompare(a.name_en));
    else if (sort === 'books_desc') items = [...items].sort((a, b) => (b.booksCount ?? 0) - (a.booksCount ?? 0));
    else if (sort === 'books_asc') items = [...items].sort((a, b) => (a.booksCount ?? 0) - (b.booksCount ?? 0));
    return items;
  }, [rawCategories, localSearch, sortValues.sort]);

  const isPending = addCategory.isPending || updateCategory.isPending;

  return (
    <div className="space-y-4">
      <AppCard>
        <div className="flex items-center justify-between border-b border-coal-200 px-5 py-4">
          <div className="flex items-center gap-2">
            <Tag className="h-5 w-5 text-primary" />
            <h1 className="text-lg font-semibold text-coal-900">Categories</h1>
            {data?.pagination?.totalItems != null && (
              <span className="rounded-full bg-coal-100 px-2 py-0.5 text-xs font-medium text-coal-500">
                {data.pagination.totalItems}
              </span>
            )}
          </div>
          <AppButton size="sm" onClick={openAdd}>
            <Plus className="h-4 w-4" />
            Add Category
          </AppButton>
        </div>

        {/* Search + sort bar */}
        <div className="flex flex-col gap-2 border-b border-coal-100 px-5 py-3 sm:flex-row sm:items-center sm:justify-between">
          <AppInput
            placeholder="Search categories..."
            icon={Search}
            className="sm:w-56"
            value={localSearch}
            onChange={e => setLocalSearch(e.target.value)}
          />
          <TableFilterBar
            filters={SORT_CONFIGS}
            values={sortValues}
            onChange={(k, v) => setSortValues(prev => ({ ...prev, [k]: v }))}
            onClearAll={() => setSortValues({ sort: '' })}
          />
        </div>

        <div className="divide-y divide-coal-100">
          {isLoading
            ? Array.from({ length: 8 }).map((_, i) => (
                <div key={i} className="flex items-center gap-4 px-5 py-3">
                  <Skeleton className="h-4 w-6 rounded" />
                  <Skeleton className="h-4 w-40 rounded flex-1" />
                  <Skeleton className="h-4 w-28 rounded" />
                  <Skeleton className="h-8 w-20 rounded" />
                </div>
              ))
            : categories.length === 0
              ? (
                <p className="py-10 text-center text-sm text-coal-400">No categories found</p>
              )
              : categories.map((cat, idx) => (
                <div key={cat._id} className="flex items-center gap-4 px-5 py-3 hover:bg-coal-50 transition-colors">
                  <span className="w-6 text-center text-xs font-medium text-coal-400">
                    {cat.index ?? idx + 1}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-coal-800">{cat.name_en}</p>
                    <p className="text-xs text-coal-400 font-arabic">{cat.name_ar}</p>
                  </div>
                  {cat.booksCount != null && (
                    <span className="text-xs text-coal-400">{cat.booksCount} books</span>
                  )}
                  <div className="flex gap-1.5">
                    <AppButton size="xs" isIconOnly onClick={() => openEdit(cat)}>
                      <Pencil className="h-3.5 w-3.5 text-warning" />
                    </AppButton>
                    <AppButton size="xs" isIconOnly onClick={() => setDeleteTarget(cat)}>
                      <Trash2 className="h-3.5 w-3.5 text-danger" />
                    </AppButton>
                  </div>
                </div>
              ))}
        </div>

        {data?.pagination && (
          <div className="flex justify-center border-t border-coal-200 py-3">
            <AppPagination
              isLoading={isLoading}
              currentPage={data.pagination.currentPage}
              totalPages={data.pagination.totalPages}
              onPageChange={setPage}
            />
          </div>
        )}
      </AppCard>

      {/* Add / Edit Modal */}
      {showModal && (
        <>
          <div className="fixed inset-0 z-50 bg-coal-900/40 backdrop-blur-sm" onClick={closeModal} />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="w-full max-w-sm rounded-xl border border-coal-200 bg-white shadow-dropdown">
              <div className="flex items-center justify-between border-b border-coal-200 px-6 py-4">
                <h3 className="text-base font-semibold text-coal-900">
                  {editTarget ? 'Edit Category' : 'Add Category'}
                </h3>
                <button className="rounded-md p-1.5 text-coal-400 hover:bg-coal-100" onClick={closeModal}>
                  <X className="h-4 w-4" />
                </button>
              </div>
              <form onSubmit={handleSubmit} className="flex flex-col gap-4 p-6">
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-coal-700">English Name *</label>
                  <AppInput
                    placeholder="e.g. Mathematics"
                    value={form.name_en}
                    onChange={e => setForm(p => ({ ...p, name_en: e.target.value }))}
                  />
                </div>
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-coal-700">Arabic Name *</label>
                  <AppInput
                    placeholder="مثال: رياضيات"
                    dir="rtl"
                    value={form.name_ar}
                    onChange={e => setForm(p => ({ ...p, name_ar: e.target.value }))}
                  />
                </div>
                <div className="flex gap-3 pt-1">
                  <AppButton type="submit" loading={isPending} className="flex-1">
                    <Save className="h-4 w-4" />
                    {editTarget ? 'Update' : 'Create'}
                  </AppButton>
                  <AppButton type="button" theme="default" onClick={closeModal} className="flex-1">Cancel</AppButton>
                </div>
              </form>
            </div>
          </div>
        </>
      )}

      {/* Delete Confirm */}
      {deleteTarget && (
        <>
          <div className="fixed inset-0 z-50 bg-coal-900/40 backdrop-blur-sm" onClick={() => setDeleteTarget(null)} />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="w-full max-w-sm rounded-xl border border-coal-200 bg-white shadow-dropdown p-6 text-center">
              <Trash2 className="mx-auto mb-3 h-10 w-10 text-danger" />
              <h3 className="text-base font-semibold text-coal-900 mb-2">Delete Category</h3>
              <p className="text-sm text-coal-500 mb-5">
                Are you sure you want to delete <strong>"{deleteTarget.name_en}"</strong>? This cannot be undone.
              </p>
              <div className="flex gap-3">
                <AppButton
                  theme="error"
                  className="flex-1"
                  loading={deleteCategory.isPending}
                  onClick={() => deleteCategory.mutate(deleteTarget._id)}
                >
                  Delete
                </AppButton>
                <AppButton theme="default" className="flex-1" onClick={() => setDeleteTarget(null)}>Cancel</AppButton>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
